import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Skeleton from "@mui/material/Skeleton";
import Dialog from "@mui/material/Dialog";

import {
  getMember,
  updateMember,
  getReservedFlights,
  getReservedFlightByRecord,
} from "api/Member";
import { createTikkie, getAllTikkieRequests } from "api/Payment";

import useApi from "hooks/useApi";
import { useTranslation } from "i18n";
import {
  removeUserFromLocalStorage,
  getUserFromLocalStorage,
} from "hooks/LocalStorage";

import { checkISAdminOrEmployee } from "utils/Utils";

import ChangePassword from "components/change-password";
import ReservedFlightDetails from "components/reservedFlightDetails";
import "./style.css";

import TikkieTab from "./tabs/tikkie-tab";
import ActivitiesTab from "./tabs/activities-tab";
import ProfileTab from "./tabs/profile-tab";
import PassportManager from "./tabs/passport-manager";
import FlightDetails from "./tabs/flight-details";
import Reports from "./tabs/reports";
import Stripe from "./tabs/stripe";

const Account = ({}) => {
  const { tabActive } = useParams();
  const { t } = useTranslation();
  var utc = require("dayjs/plugin/utc");
  dayjs.extend(utc);

  const [tab, setTab] = useState(tabActive ?? "profile");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [open, setOpen] = useState(false);
  const [changePass, setChangePass] = useState(false);
  const [info, setInfo] = useState(false);

  const getMemberInfo = useApi(getMember);
  const getReservedFlightDetails = useApi(getReservedFlightByRecord);
  const getAllReservedFlights = useApi(getReservedFlights);
  const fetchAllTikkieRequests = useApi(getAllTikkieRequests);

  useEffect(() => {
    if (tab === "profile") {
      getMemberInfo.request();
    }
    if (tab === "my-activity") {
      getAllReservedFlights.request();
    }
    if (tab === "tikkie") {
      fetchAllTikkieRequests.request();
    }
  }, [getMemberInfo.error, tab]);

  const handleChange = (newValue) => {
    setTab(newValue);
    setSuccess("");
  };

  const handelClickOpen = (recordLocator) => {
    setOpen(true);
    getReservedFlightDetails.request({
      recordLocator,
    });
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required().label(t("account.firstName")),
    lastName: Yup.string().required().label(t("account.lastName")),
    dateOfBirthDay: Yup.string().nullable().max(2).label(t("account.dateOfBirth")),
    dateOfBirthMonth: Yup.string().nullable().label(t("account.dateOfBirth")),
    dateOfBirthYear: Yup.string().min(4).max(4).label(t("account.dateOfBirth")),
    phoneNumber: Yup.string().required().label(t("account.phoneNumber")),
    email: Yup.string().email().required().label(t("account.email")),
    passportNumber: Yup.string().label(t("account.passportNumber")),
  });

  const validationSchemaForTikkie = Yup.object().shape({
    description: Yup.string()
      .required()
      .max(35, "Description should not be more than 35 characters")
      .label("Description"),
    amountInCents: Yup.string().required().label("Amount"),
    expiryDate: Yup.string().nullable().label("Expiration Date"),
  });

  const monthsLong = {
    January: "01",
    February: "02",
    March: "03",
    April: "04",
    May: "05",
    June: "06",
    July: "07",
    August: "08",
    September: "09",
    October: "10",
    November: "11",
    December: "12",
  };

  const handelSubmit = async (values) => {
    setLoading(true);
    setError("");
    setSuccess("");
    const body = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth:
        (values.dateOfBirthYear ??
          dayjs(getMemberInfo?.data?.memberDetails?.dateOfBirth).format(
            "YYYY"
          )) +
        "-" +
        (values.dateOfBirthMonth
          ? monthsLong[values.dateOfBirthMonth]
          : dayjs(getMemberInfo?.data?.memberDetails?.dateOfBirth).format(
              "MM"
            )) +
        "-" +
        (values.dateOfBirthDay ??
          dayjs(getMemberInfo?.data?.memberDetails?.dateOfBirth).format("DD")),

      phoneNumber:
        values.phoneNumber.charAt(0) === "+"
          ? values.phoneNumber
          : "+" + values.phoneNumber,
      passportNumber: values.passportNumber,
    };
    const res = await updateMember({
      body,
    });
    if (!res.ok) {
      setError(res?.data?.errorDetails);
    } else {
      setSuccess(t("account.updateSuccess"));
    }
    setLoading(false);
  };

  const handleCreateTikkie = async (values) => {
    setLoading(true);
    const amountInCents = Number(
      (parseFloat(values.amountInCents) * 100).toFixed(0)
    );
    const body = {
      amountInCents,
      description: values.description,
      expiryDate: values.expiryDate
        ? dayjs(values.expiryDate).format("YYYY-MM-DD")
        : dayjs(
            new Date(new Date().getTime() + 2 * 24 * 60 * 60 * 1000)
          ).format("YYYY-MM-DD"),
      referenceId: uuidv4()?.slice(6),
      email: getMemberInfo?.data?.memberDetails?.email,
    };
    const response = await createTikkie({ body });
    if (!response.ok) {
      setError("There is something wrong. Please try again later!");
    } else {
      const oldTikkieRequests = [
        ...fetchAllTikkieRequests?.data?.myPaymentRequests,
      ];
      if (navigator.clipboard) {
        navigator.clipboard.writeText(response?.data?.url);
      }

      const dataObj = {
        myPaymentRequests: oldTikkieRequests,
      };

      oldTikkieRequests.unshift(response?.data);
      fetchAllTikkieRequests?.setData(dataObj);
      setSuccess(`Your Tikkie request created successfully!`);
      setInfo("The created tikkie link was successfully copied to clipboard!");
    }
    setLoading(false);
  };

  const handleShowPassword = () => {
    setChangePass(true);
  };

  const tabs = [
    {
      label: "My Account",
      labelKey: "account.tabs.myAccount",
      value: "profile",
      permission: "user",
    },
    {
      label: "My Activities",
      labelKey: "account.tabs.myActivities",
      value: "my-activity",
      permission: "user",
    },
    {
      label: "Tikkie",
      labelKey: "account.tabs.tikkie",
      value: "tikkie",
      permission: checkISAdminOrEmployee(),
    },
    {
      label: "Stripe",
      labelKey: "account.tabs.stripe",
      value: "stripe",
      permission: checkISAdminOrEmployee(),
    },
    {
      label: "Passport Manager",
      labelKey: "account.tabs.passportManager",
      value: "passport-manager",
      permission: checkISAdminOrEmployee(),
    },
    {
      label: "PNR Manager",
      labelKey: "account.tabs.pnrManager",
      value: "pnr-manager",
      permission:
        checkISAdminOrEmployee() ||
        getUserFromLocalStorage()?.type === "B_AGENT",
    },
    {
      label: "Reports",
      labelKey: "account.tabs.reports",
      value: "reports",
      permission: getUserFromLocalStorage()?.type === "ADMIN",
    },
  ];

  const renderTab = () => {
    const allTabs = {
      profile: changePass ? (
        <ChangePassword setChangePass={setChangePass} />
      ) : (
        <ProfileTab
          handelSubmit={handelSubmit}
          validationSchema={validationSchema}
          error={error}
          success={success}
          handleShowPassword={handleShowPassword}
          loading={loading}
          getMemberInfo={getMemberInfo?.data?.memberDetails}
        />
      ),
      "my-activity": (
        <ActivitiesTab
          getAllReservedFlights={getAllReservedFlights}
          handelClickOpen={handelClickOpen}
        />
      ),

      tikkie: (
        <TikkieTab
          error={error}
          success={success}
          handleSubmit={handleCreateTikkie}
          validationSchema={validationSchemaForTikkie}
          loading={loading}
          fetchAllTikkieRequests={fetchAllTikkieRequests}
          info={info}
        />
      ),

      stripe: <Stripe />,
      "passport-manager": <PassportManager />,
      "pnr-manager": <FlightDetails />,
      reports: <Reports />,
    };

    return allTabs[tab];
  };

  const member = getMemberInfo?.data?.memberDetails;

  return (
    <>
      <div className="bg account-page">
        <Container>
          {getMemberInfo.error ? (
            <Box py={7}>
              <div className="empty-data-msg">
                <h1 className="medium-font-size title-header">
                  {t("account.sessionExpired")}
                </h1>
              </div>
            </Box>
          ) : (
            <Box py={7}>
              <header className="account-hero">
                {getMemberInfo?.loading ? (
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                    <Skeleton
                      variant="rounded"
                      width={72}
                      height={72}
                      animation="wave"
                      sx={{ bgcolor: "#E8EEF4" }}
                    />
                    <Box>
                      <Skeleton width={180} height={28} animation="wave" />
                      <Skeleton width={140} height={22} animation="wave" />
                    </Box>
                  </Box>
                ) : (
                  <>
                    <p className="account-hero__eyebrow">{t("account.eyebrow")}</p>
                    <div className="account-hero__row">
                      <Avatar className="account-hero__avatar">
                        {member?.firstName?.charAt(0)}
                        {member?.lastName?.charAt(0)}
                      </Avatar>
                      <div>
                        <h1>
                          {member?.firstName} {member?.lastName}
                        </h1>
                        <p>
                          {t("account.memberId")} {member?.memberId}
                          {member?.memberSince
                            ? ` · ${t("account.memberSince")} ${member.memberSince}`
                            : ""}
                        </p>
                      </div>
                    </div>
                    <dl className="account-hero__stats">
                      <div>
                        <dt>{t("account.availablePoints")}</dt>
                        <dd>{member?.travelPoints ?? "0"}</dd>
                      </div>
                      <div>
                        <dt>{t("account.currentStatus")}</dt>
                        <dd>{member?.status || "—"}</dd>
                      </div>
                    </dl>
                  </>
                )}
              </header>

              <nav className="account-tabs" aria-label={t("account.tabs.myAccount")}>
                {tabs?.map(
                  (item) =>
                    item.permission && (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => handleChange(item.value)}
                        className={tab == item.value ? "is-active" : ""}
                      >
                        {item.labelKey ? t(item.labelKey) : item.label}
                      </button>
                    )
                )}
              </nav>

              {getMemberInfo?.loading ? (
                <div className="account-panel">
                  <Skeleton width="40%" height={28} animation="wave" />
                  <Skeleton width="70%" height={22} animation="wave" />
                  <Skeleton width="55%" height={22} animation="wave" />
                </div>
              ) : (
                <div className="account-panel">{renderTab()}</div>
              )}
            </Box>
          )}
        </Container>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="dialog-container"
      >
        <Box p={4}>
          <ReservedFlightDetails
            loading={getReservedFlightDetails?.loading}
            reservation={getReservedFlightDetails?.data?.reservation}
            price={getReservedFlightDetails?.data?.reservation?.price}
          />
        </Box>
      </Dialog>
    </>
  );
};

export default Account;
